import { CORE_SETUP_LABELS } from '../../app/terminology/setup';
import { getComponentLabel } from '../../app/terminology/components';
import type { SetupHealth } from '../../app/models/SetupHealth';

type SetupStep = { label: string; status: 'complete' | 'action' | 'unknown' };

// Keep these aligned with the setup checklist and the API's required core checks.
export const getCoreSetupSteps = ({ core }: SetupHealth): SetupStep[] => [
    { label: CORE_SETUP_LABELS.fields, status: core.enabledFieldCount > 0 ? 'complete' : 'action' },
    { label: CORE_SETUP_LABELS.records, status: core.fitmentRecordCount > 0 || core.referenceDataEnabled ? 'complete' : 'action' },
    { label: CORE_SETUP_LABELS.products, status: core.productFitmentAssignmentCount > 0 || core.externalProductLinkCount > 0 || core.universalProductCount > 0 ? 'complete' : 'action' },
    { label: CORE_SETUP_LABELS.storefront, status: core.storefrontConfigured && core.storefrontEnabled ? 'complete' : 'action' },
];

export function getDashboardSetupStatus(health: SetupHealth) {
    const core = getCoreSetupSteps(health);
    const steps = [...core];
    // The endpoint defaults to the published theme, but can fall back to a draft
    // when no published theme exists. Never treat that draft as live-store health.
    if (!health.themeInspectionAvailable || !health.selectedTheme?.isPublished) {
        steps.push({ label: 'Verify published theme installation', status: 'unknown' });
    } else {
        steps.push({
            label: 'Review published theme compatibility',
            status: health.selectedTheme.supportsJsonTemplates ? 'complete' : 'action',
        });
        for (const component of health.components.filter(item => item.required && item.applicable)) {
            steps.push({
                label: `Add ${getComponentLabel(component.key, component.label)} to your published theme`,
                status: component.installed === true ? 'complete' : component.installed === false ? 'action' : 'unknown',
            });
        }
    }
    const pending = steps.filter(step => step.status === 'action');
    const unverified = steps.some(step => step.status === 'unknown');
    return {
        pending,
        unverified,
        complete: steps.filter(step => step.status === 'complete').length,
        total: steps.length,
        coreComplete: core.every(step => step.status === 'complete'),
        ready: pending.length === 0 && !unverified,
    };
}
